import { Lang } from '../services/i18n.service';

/**
 * A branch's next opening time (`status.opens_at`), as a customer reads it:
 *   today      → "Opens at 1:00 PM"           / "يفتح 1:00 م"
 *   tomorrow   → "Opens tomorrow at 1:00 PM"  / "يفتح غدًا 1:00 م"
 *   this week  → "Opens Fri at 1:00 PM"       / "يفتح الجمعة 1:00 م"
 *   later      → "Opens Oct 21 at 1:00 PM"    / "يفتح 21 أكتوبر 1:00 م"
 * Accepts the API's ISO date-time ("2026-10-16T13:00:00+03:00") or a bare "13:00[:00]"
 * (read as today). Anything unparseable is shown as-is after "Opens at".
 */
export function formatOpensAt(value: string | null | undefined, lang: Lang, now = new Date()): string {
  if (!value) return '';
  const ar = lang === 'ar';
  // Arabic words with Western digits (1:00), like prices and times everywhere else in the app
  const locale = ar ? 'ar-EG-u-nu-latn' : 'en-US';
  const at = parseOpensAt(value, now);
  if (!at) return ar ? `يفتح ${value}` : `Opens at ${value}`;

  const time = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(at);
  const days = dayDiff(now, at);
  if (days <= 0) return ar ? `يفتح ${time}` : `Opens at ${time}`;
  if (days === 1) return ar ? `يفتح غدًا ${time}` : `Opens tomorrow at ${time}`;
  const day = days < 7
    ? new Intl.DateTimeFormat(locale, { weekday: ar ? 'long' : 'short' }).format(at)
    : new Intl.DateTimeFormat(locale, { month: ar ? 'long' : 'short', day: 'numeric' }).format(at);
  return ar ? `يفتح ${day} ${time}` : `Opens ${day} at ${time}`;
}

/** "Closed · Opens tomorrow at 1:00 PM", or just "Closed" / "مغلق". */
export function closedLabel(opensAt: string | null | undefined, lang: Lang): string {
  const closed = lang === 'ar' ? 'مغلق' : 'Closed';
  const opens = formatOpensAt(opensAt, lang);
  return opens ? `${closed} · ${opens}` : closed;
}

function parseOpensAt(value: string, now: Date): Date | null {
  const hm = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(value.trim());
  if (hm) {
    const d = new Date(now);
    d.setHours(+hm[1], +hm[2], 0, 0);
    return d;
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** Whole calendar days from `a` to `b` in the device's time zone (0 = same day). */
function dayDiff(a: Date, b: Date): number {
  const start = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((start(b) - start(a)) / 86_400_000);
}
